import { cameraOverviewHttpController } from '@controllers/index';
import { CameraServiceType } from '@EcamModel/model';
import { ConsolidateCameraMonitoring } from '@EcamModel/model/CameraOverview';
import { useMutation, useQuery, useQueryClient } from 'react-query';

export const CAMERA_DATA_QUERY_KEY = 'cameraConfigMonitoring';

export const useCameraData = (startDate: Date, endDate: Date) => {
    const queryClient = useQueryClient();

    const {
        data: cameraData = [],
        isLoading,
        error,
        refetch,
    } = useQuery<ConsolidateCameraMonitoring[]>(
        [CAMERA_DATA_QUERY_KEY, startDate, endDate],
        async () => {
            const result = await cameraOverviewHttpController.getConsolidateCameraMonitoring({
                startDate,
                endDate,
            });

            return result.map((row) =>
                row.CameraService === CameraServiceType.PTZ
                    ? { ...row, LastContact: row.PTZLastContact }
                    : row
            );
        },
        {
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
        }
    );

    const updateCameraData = useMutation(
        (updatedCamera: ConsolidateCameraMonitoring) => {
            return Promise.resolve(updatedCamera);
        },
        {
            onMutate: async (updatedCamera) => {
                await queryClient.cancelQueries([CAMERA_DATA_QUERY_KEY]);

                const previousData = queryClient.getQueryData<ConsolidateCameraMonitoring[]>([
                    CAMERA_DATA_QUERY_KEY,
                    startDate,
                    endDate,
                ]);

                queryClient.setQueryData<ConsolidateCameraMonitoring[]>(
                    [CAMERA_DATA_QUERY_KEY, startDate, endDate],
                    (old) => {
                        if (!old) return [];
                        return old.map((camera) =>
                            camera.CameraId === updatedCamera.CameraId ? updatedCamera : camera
                        );
                    }
                );

                return { previousData };
            },
            onError: (err, updatedCamera, context) => {
                if (context?.previousData) {
                    queryClient.setQueryData([CAMERA_DATA_QUERY_KEY, startDate, endDate], context.previousData);
                }
            },
            onSettled: () => {
                queryClient.invalidateQueries([CAMERA_DATA_QUERY_KEY]);
            },
        }
    );

    const updateSingleCamera = (cameraId: number, updates: Partial<ConsolidateCameraMonitoring>) => {
        const currentData = queryClient.getQueryData<ConsolidateCameraMonitoring[]>([
            CAMERA_DATA_QUERY_KEY,
            startDate,
            endDate,
        ]);

        if (currentData) {
            const updatedCamera = currentData.find((camera) => camera.CameraId === cameraId);
            if (updatedCamera) {
                const newCameraData = { ...updatedCamera, ...updates };
                updateCameraData.mutate(newCameraData);
            }
        }
    };

    return {
        cameraData,
        isLoading,
        error,
        refetch,
        updateSingleCamera,
        isUpdating: updateCameraData.isLoading,
    };
};
